import { Briefcase, Users, Building } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const audiences = [
  {
    icon: Briefcase,
    title: "Consultants & Freelancers",
    description:
      "You receive many inbound emails and need to capture contact details fast — without learning a new system.",
  },
  {
    icon: Users,
    title: "Sales & Recruiting Teams",
    description:
      "You work directly from Outlook and need accurate contact data with consistent tags and presets.",
  },
  {
    icon: Building,
    title: "Small Businesses",
    description: "You need clean contacts without CRM complexity, mandatory subscriptions or seat-based pricing.",
  },
];

const AudienceCard = ({ audience, index }: { audience: typeof audiences[0]; index: number }) => {
  const { ref, animationClass } = useScrollAnimation<HTMLDivElement>({
    type: "slide-up",
    delay: index * 100,
    threshold: 0.15,
  });

  return (
    <div ref={ref} className={`fluent-tile fluent-tile-hover p-6 md:p-7 bg-card group ${animationClass}`}>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-surface-2 border border-border grid place-items-center flex-shrink-0">
          <audience.icon className="w-5 h-5 text-foreground" strokeWidth={1.75} />
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-display font-semibold mb-2">{audience.title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{audience.description}</p>
        </div>
      </div>
    </div>
  );
};

export const TargetAudience = () => {
  const { ref: headerRef, animationClass: headerAnimation } = useScrollAnimation<HTMLDivElement>({
    type: "slide-up",
    threshold: 0.3,
  });

  return (
    <section className="py-20 md:py-28 bg-surface-1">
      <div className="container px-4">
        <div ref={headerRef} className={`max-w-2xl mb-12 md:mb-16 ${headerAnimation}`}>
          <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
            Who it's for
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold mt-3 mb-5">
            Built for professionals who live in Outlook.
          </h2>
          <p className="text-base md:text-lg text-muted-foreground">
            No new system to learn. Works where you already work.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-5 max-w-6xl">
          {audiences.map((audience, index) => (
            <AudienceCard key={index} audience={audience} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
